import type { ReleaseTag, RollbackResigningStrategy } from '@/types';

export type HotfixRequestStatus = 'pending' | 'approving' | 'approved' | 'rejected' | 'cancelled';
export type HotfixRequestOrigin = 'branchDetection';
export const ACTIVE_HOTFIX_REQUEST_STATUSES: HotfixRequestStatus[] = ['pending', 'approving'];
export type HotfixProposalDestinationAction = 'include' | 'exclude';
export interface HotfixProposalDestinationOptions {
    destinationIds: string[];
    action: HotfixProposalDestinationAction;
}
export type HotfixProposalBase = Omit<ReleaseTag, 'authorEmail' | 'authorName'> & {
    authorEmail?: string | null;
    authorName?: string | null;
};
export interface HotfixProposal {
    releaseType: 'hotfix' | 'rollback';
    proposedVersion: string;
    releaseName?: string;
    base?: HotfixProposalBase;
    shouldCreateBranch: boolean;
    shouldBumpVersionOnBranch: boolean;
    commitsToCherryPick: string[] | null;
    rollbackSourceVersion?: string;
    rollbackResigningStrategy?: RollbackResigningStrategy;
    shouldMatchPreviousPhasedRelease: boolean;
    destinationOptions?: HotfixProposalDestinationOptions;
}
export interface HotfixRequestApprovalReleaseIdentity {
    internalVersion: { MajorNum: number; MinorNum: number; PointNum: number; ExtraNum: number };
    expectedReleaseId: string;
    rollbackSourceVersion?: string;
}
export interface HotfixRequest {
    id: string;
    orgId: string;
    appId: string;
    status: HotfixRequestStatus;
    origin?: HotfixRequestOrigin;
    proposal: HotfixProposal;
    justification: string;
    formFieldValues?: HotfixRequestFormFieldValue[];
    finalProposal?: HotfixProposal;
    approvalAttemptProposal?: HotfixProposal;
    approvalReleaseIdentity?: HotfixRequestApprovalReleaseIdentity;
    approvalAttemptId?: string;
    approvalExpectedRevision?: number;
    approvalStartedAt?: string;
    approvalStartedBy?: string;
    approvalCompletedAt?: string;
    approvalCompletedBy?: string;
    lastApprovalError?: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    revision: number;
    terminalReason?: string;
    terminalAt?: string;
    terminalBy?: string;
    resultingReleaseId?: string;
    policySnapshot: HotfixRequestPolicySnapshot;
    approvalCount: number;
}
export interface HotfixRequestFormFieldSettings {
    name: string;
    required: boolean;
    minCharacters?: number;
    description?: string;
    placeholder?: string;
    prePopulatedText?: string;
}
export interface HotfixRequestFormFieldValue {
    fieldId: string;
    value: string;
}
export interface HotfixRequestSettings {
    hotfixApprovalsEnabled: boolean;
    requiredHotfixRequestApprovals: number;
    hotfixApprovalRequiredApproverUserIds: string[];
    hotfixApprovalRequiredApproverUserGroupIds: string[];
    hotfixApprovalImplicitCreatorApproval: boolean;
    hotfixRequestFormFieldsSettings: HotfixRequestFormFieldSettings[];
}
export interface HotfixRequestPolicySnapshot {
    approvalsRequired: boolean;
    requiredApprovalCount: number;
    requiredApproverUserIds: string[];
    requiredApproverUserGroupIds: string[];
    implicitCreatorApproval: boolean;
    formFieldsSettings: HotfixRequestFormFieldSettings[];
}
export type HotfixRequestAuditEventType =
    | 'requestCreated'
    | 'requestEdited'
    | 'requestRejected'
    | 'requestCancelled'
    | 'approvalStarted'
    | 'approvalFailed'
    | 'approvalSucceeded'
    | 'approvalRecorded';
export interface HotfixRequestAuditEvent {
    id: string;
    requestId: string;
    orgId: string;
    appId: string;
    type: HotfixRequestAuditEventType;
    actorId: string;
    reason?: string;
    approvalAttemptId?: string;
    finalProposal?: HotfixProposal;
    resultingReleaseId?: string;
    createdAt: string;
}
export interface HotfixRequestDetail {
    request: HotfixRequest;
    auditEvents: HotfixRequestAuditEvent[];
    approvals: HotfixRequestApproval[];
}
export interface HotfixRequestApproval {
    id: string;
    requestId: string;
    orgId: string;
    appId: string;
    actorId: string;
    createdAt: string;
}
export interface HotfixRequestScope {
    orgId: string;
    appId: string;
}
export interface CreateHotfixRequestBody {
    proposal: HotfixProposal;
    justification: string;
    formFieldValues?: HotfixRequestFormFieldValue[];
}
export type UpdateHotfixRequestSettingsBody = HotfixRequestScope & Partial<HotfixRequestSettings>;
export interface HotfixRequestMutationScope extends HotfixRequestScope {
    requestId: string;
}
export interface RejectHotfixRequestBody extends HotfixRequestMutationScope {
    reason: string;
}
export interface ApproveHotfixRequestBody extends HotfixRequestMutationScope {
    expectedRevision: number;
    finalProposal: HotfixProposal;
}
export interface UpdateHotfixRequestBody extends HotfixRequestMutationScope, CreateHotfixRequestBody {
    expectedRevision: number;
}
export interface HotfixRequestSafeError {
    message: string;
}
